import '@fontsource-variable/geist'
import '@fontsource-variable/geist-mono'
import 'vue-sonner/style.css'
import '@filebuddy/tour/theme.css'
import '../css/app.css'
import './bootstrap'
import './bootstrap-errors'

import { createInertiaApp, router, usePage } from '@inertiajs/vue3'
import { vOnLongPress } from '@vueuse/components'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { createPinia } from 'pinia'
import {
  createApp,
  type DefineComponent,
  h,
  ref,
  watch,
  type WritableComputedRef,
} from 'vue'
import { toast } from 'vue-sonner'

import { Toaster } from '@/components/ui/sonner'

import { initializeTheme } from './composables/useAppearance'
import { useInternalAnalytics } from './composables/useInternalAnalytics'
import { formatPageTitle, resolveAppName } from './lib/appName'
import { syncDomFavicon } from './lib/favicon'
import { i18n, t } from './lib/i18n'
import {
  capturePostHogException,
  initPostHog,
  syncPostHogUser,
} from './lib/posthog'
import en from './locales/en.json'

// The server always shares `name` (config('app.name')); it is resolved from the
// initial page props inside setup() before anything renders, so this module-init
// placeholder is never displayed.
let appName = ''

const pinia = createPinia()

const SUPPORTED_LOCALES = [
  'de',
  'en',
  'es',
  'fr',
  'pt',
  'ja',
  'zh',
  'it',
] as const
type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]
type LocaleMessages = typeof en

const isSupportedLocale = (locale: unknown): locale is SupportedLocale => {
  return (
    typeof locale === 'string' &&
    SUPPORTED_LOCALES.includes(locale as SupportedLocale)
  )
}

const loadedLocales: string[] = ['en']

const localeModules = import.meta.glob([
  './locales/*.json',
  '!./locales/en.json',
])

async function loadLocaleMessages(locale: SupportedLocale) {
  if (loadedLocales.includes(locale)) {
    return
  }

  const path = `./locales/${locale}.json`
  if (path in localeModules) {
    const messages = (await localeModules[path]()) as {
      default: LocaleMessages
    }
    i18n.global.setLocaleMessage(locale, messages.default)
    loadedLocales.push(locale)
  }
}

const { trackPageView, initHeartbeat } = useInternalAnalytics()

const getSanitizedPath = (url: string): string => {
  try {
    return new URL(url, globalThis.location.origin).pathname
  } catch {
    return url.split('?')[0] ?? url
  }
}

function detectDeviceLocale(): SupportedLocale {
  if (typeof navigator === 'undefined') return 'en'

  const userLangs = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
  ].filter(Boolean)

  for (const lang of userLangs) {
    const code = lang.toLowerCase().split(/[-_]/)[0]
    if (isSupportedLocale(code)) {
      return code
    }
  }

  return 'en'
}

async function initLocale(locale: unknown): Promise<void> {
  const resolvedLocale: SupportedLocale = isSupportedLocale(locale)
    ? locale
    : detectDeviceLocale()
  await loadLocaleMessages(resolvedLocale)
  ;(i18n.global.locale as WritableComputedRef<string>).value = resolvedLocale
}

void createInertiaApp({
  title: (title) => formatPageTitle(title, appName),
  resolve: (name) =>
    resolvePageComponent(
      `./pages/${name}.vue`,
      import.meta.glob<DefineComponent>('./pages/**/*.vue'),
    ),
  defaults: {
    prefetch: {
      cacheFor: '1m',
      hoverDelay: 300,
    },
  },
  async setup({ el, App, props, plugin }) {
    const initialProps = props.initialPage?.props
    appName = resolveAppName(initialProps ?? {})
    const initialLocale = initialProps?.locale
    const page = usePage()

    await initLocale(initialLocale)

    const app = createApp({
      render: () => h('div', [h(App, props), h(Toaster, { visibleToasts: 1 })]),
    })
      .use(plugin)
      .use(pinia)
      .use(i18n)
      .directive('on-long-press', vOnLongPress)
      .directive('long-press', vOnLongPress)

    app.config.errorHandler = (error, _instance, info) => {
      console.error('[Vue Error]', error, info)
      capturePostHogException(error, { vueInfo: info })
    }

    watch(
      () => page.props?.locale,
      async (newLocale) => {
        await initLocale(newLocale)
      },
    )

    // Global flash message and validation error watchers
    const lastSuccess = ref<string | null>(null)
    const lastError = ref<string | null>(null)

    watch(
      () => page.props?.flash,
      (flash) => {
        if (flash?.success && flash.success !== lastSuccess.value) {
          toast.success(t('common.success'), {
            description: flash.success,
          })
          lastSuccess.value = flash.success
        } else if (!flash?.success) {
          lastSuccess.value = null
        }

        if (flash?.error && flash.error !== lastError.value) {
          toast.error(t('common.error'), {
            description: flash.error,
          })
          lastError.value = flash.error
        } else if (!flash?.error) {
          lastError.value = null
        }
      },
      { deep: true },
    )

    watch(
      () => page.props?.errors,
      (errors) => {
        if (errors && Object.keys(errors).length > 0) {
          Object.values(errors).forEach((error) => {
            if (typeof error === 'string') {
              toast.error(t('common.error'), {
                description: error,
              })
            }
          })
        }
      },
      { deep: true },
    )

    watch(
      () => page.props?.auth?.user,
      () => {
        syncPostHogUser(page.props)
      },
      { deep: true },
    )

    if (initialProps) {
      initPostHog(initialProps)
    }

    app.mount(el)

    // Initialize heartbeat for unique visitor tracking; clean up on page unload
    const stopHeartbeat = initHeartbeat()
    window.addEventListener('beforeunload', stopHeartbeat, { once: true })
  },
  progress: {
    delay: 500,
    color: '#4B5563',
    includeCSS: false,
    showSpinner: false,
  },
})

// This will set light / dark mode on page load...
initializeTheme()

// Track transient edge deployment / cold start retries
let transientErrorRetries = 0
const MAX_TRANSIENT_RETRIES = 2

interface VisitRequest {
  isPrefetch?: () => boolean
  requestParams?: {
    all?: () => {
      url?: unknown
    }
  }
}

interface RouterWithStreams {
  asyncRequestStream?: {
    requests?: VisitRequest[]
  }
  syncRequestStream?: {
    requests?: VisitRequest[]
  }
}

function matchesPrefetchUrl(
  req: VisitRequest,
  failedUrl: string | null,
): boolean {
  if (typeof req.isPrefetch !== 'function' || !req.isPrefetch()) {
    return false
  }
  const reqUrl = req.requestParams?.all?.()?.url
  const reqUrlStr =
    reqUrl instanceof URL
      ? reqUrl.href
      : typeof reqUrl === 'string'
        ? reqUrl
        : reqUrl && typeof reqUrl === 'object' && 'href' in reqUrl
          ? String((reqUrl as Record<string, unknown>).href)
          : reqUrl
            ? String(reqUrl)
            : ''
  return reqUrlStr.split('#')[0] === failedUrl
}

function isRouterPrefetchRequest(failedUrl: string | null): boolean {
  const routerWithStreams = router as unknown as RouterWithStreams
  const inAsync = routerWithStreams.asyncRequestStream?.requests?.some((req) =>
    matchesPrefetchUrl(req, failedUrl),
  )
  if (inAsync) {
    return true
  }

  return (
    routerWithStreams.syncRequestStream?.requests?.some((req) =>
      matchesPrefetchUrl(req, failedUrl),
    ) ?? false
  )
}

function attemptTransientRetry(failedUrl: string | null): boolean {
  if (transientErrorRetries >= MAX_TRANSIENT_RETRIES) {
    return false
  }
  transientErrorRetries++
  const retryDelay = transientErrorRetries * 1500
  setTimeout(() => {
    if (failedUrl) {
      router.visit(failedUrl, {
        preserveScroll: true,
        preserveState: true,
        replace: true,
      })
    } else {
      router.reload({ preserveUrl: true })
    }
  }, retryDelay)
  return true
}

function isAbortException(errorObj: unknown): boolean {
  if (errorObj instanceof Error) {
    return (
      errorObj.name === 'AbortError' ||
      errorObj.name === 'HttpCancelledError' ||
      (errorObj as { code?: string }).code === 'ERR_CANCELLED' ||
      errorObj.message?.includes('Request was cancelled')
    )
  }
  if (typeof errorObj === 'object' && errorObj !== null) {
    const errorRecord = errorObj as {
      name?: string
      code?: string
      message?: string
    }
    return (
      errorRecord.name === 'AbortError' ||
      errorRecord.name === 'HttpCancelledError' ||
      errorRecord.code === 'ERR_CANCELLED' ||
      Boolean(errorRecord.message?.includes('Request was cancelled'))
    )
  }
  return false
}

const isDrivePath = (pathname: string): boolean =>
  pathname === '/drive' || pathname.startsWith('/drive/')

const isPublicSharePath = (pathname: string): boolean =>
  pathname.startsWith('/s/')

router.on('before', (event) => {
  const visit = event.detail.visit
  if ((visit.method ?? 'get').toLowerCase() !== 'get') {
    return
  }

  const currentPath = globalThis.location.pathname
  const targetPath =
    visit.url instanceof URL
      ? visit.url.pathname
      : getSanitizedPath(String(visit.url ?? ''))

  if (
    visit.component === 'Drive/Dashboard' ||
    visit.component === 'Drive/Public/ShareView' ||
    (isDrivePath(currentPath) && isDrivePath(targetPath)) ||
    (isPublicSharePath(currentPath) && isPublicSharePath(targetPath))
  ) {
    visit.showProgress = false
  }
})

router.on('success', () => {
  transientErrorRetries = 0
})

router.on('navigate', (event) => {
  trackPageView(event.detail.page.url)

  const pageSettings = event.detail.page.props.settings

  if (pageSettings?.favicon_version) {
    syncDomFavicon(pageSettings.favicon_version)
  }
})

router.on('httpException', (event) => {
  console.error('Inertia HTTP Exception', event.detail.response)
  const response = event.detail.response as unknown as
    Record<string, unknown> | undefined
  const status =
    typeof response?.status === 'number' ? response.status : 'unknown'

  const failedUrl =
    typeof response?.url === 'string'
      ? response.url
      : typeof response?.responseURL === 'string'
        ? response.responseURL
        : null
  const failedPath =
    typeof failedUrl === 'string' ? getSanitizedPath(failedUrl) : null
  const isSharePath =
    (typeof failedPath === 'string' && failedPath.startsWith('/s/')) ||
    globalThis.location.pathname.startsWith('/s/')

  const isRecoverable =
    status === 200 && isSharePath && globalThis.navigator.onLine

  if (isRecoverable) {
    event.preventDefault()
    globalThis.location.assign(failedUrl ?? globalThis.location.href)
    return
  }

  const isDeployingOrUnavailable =
    status === 502 || status === 503 || status === 504

  if (isDeployingOrUnavailable && attemptTransientRetry(failedUrl)) {
    event.preventDefault()
    return
  }

  const isClientError =
    typeof status === 'number' && status >= 400 && status < 500

  if (!isClientError) {
    capturePostHogException(new Error(`Inertia HTTP Exception: ${status}`), {
      inertiaEvent: 'httpException',
      status: String(status),
      online: String(globalThis.navigator.onLine),
      isSharePath: String(isSharePath),
      recovered: String(isRecoverable),
      currentPath: globalThis.location.pathname,
      failedUrl: typeof failedUrl === 'string' ? failedUrl : undefined,
    })
  }
})

router.on('networkError', (event) => {
  event.preventDefault()
  console.error('Inertia Network Error', event.detail.error)

  const errorObj = event.detail.error
  const failedUrl =
    errorObj && typeof errorObj === 'object' && 'url' in errorObj
      ? (errorObj.url as string)
      : null

  if (isRouterPrefetchRequest(failedUrl)) {
    return
  }

  const failedPath =
    typeof failedUrl === 'string' ? getSanitizedPath(failedUrl) : null
  const isSharePath =
    typeof failedPath === 'string' && failedPath.startsWith('/s/')

  const isRecoverable =
    typeof failedUrl === 'string' && isSharePath && globalThis.navigator.onLine

  if (isRecoverable) {
    globalThis.location.assign(failedUrl)
    return
  }

  if (globalThis.navigator.onLine && attemptTransientRetry(failedUrl)) {
    return
  }

  if (globalThis.navigator.onLine && !isAbortException(errorObj)) {
    const errorInstance =
      errorObj instanceof Error
        ? errorObj
        : typeof errorObj === 'string'
          ? new Error(errorObj)
          : new Error(
              (errorObj as { message?: string })?.message ||
                'Inertia Network Error',
            )

    capturePostHogException(errorInstance, {
      inertiaEvent: 'networkError',
      online: String(globalThis.navigator.onLine),
      isSharePath: String(isSharePath),
      recovered: String(isRecoverable),
      failedPath: failedPath ?? undefined,
      currentPath: globalThis.location.pathname,
      failedUrl: typeof failedUrl === 'string' ? failedUrl : undefined,
    })
  }
})

if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', (e) => {
    const activeEl = document.activeElement
    if (
      activeEl &&
      (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA')
    ) {
      const targetNode = e.target as Node | null
      if (
        targetNode &&
        activeEl !== targetNode &&
        !activeEl.contains(targetNode)
      ) {
        ;(activeEl as HTMLElement).blur()
      }
    }
  })

  window.addEventListener('unhandledrejection', (e) => {
    if (isAbortException(e.reason)) {
      e.preventDefault()
    }
  })

  window.addEventListener('contextmenu', (e) => {
    if (
      window.location.pathname.startsWith('/admin') ||
      (e.target instanceof HTMLElement &&
        (e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable))
    ) {
      return
    }
    e.preventDefault()
  })
}
